import React, { useState, useCallback } from "react";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCopy, faEye } from "@fortawesome/free-solid-svg-icons";
import fetch_backend from "../../../App/components/shared/Fetch";
import { styles_react_select } from "../../../App/components/shared/Select";
import { url_bill_of_materials } from "../../constants/constants";

function HistoryTab({ token, dispatch, formatCurrency, loadFromHistory, duplicateFromHistory }) {
  const [historyFilterOptions, setHistoryFilterOptions] = useState({ clients: [], quotes: [], materials: [], tags: [] });
  const [historyFiltersLoaded, setHistoryFiltersLoaded] = useState(false);
  const [historySearching, setHistorySearching] = useState(false);
  const [historyResults, setHistoryResults] = useState([]);
  const [histFilterClient, setHistFilterClient] = useState(null);
  const [histFilterQuote, setHistFilterQuote] = useState(null);
  const [histFilterMaterial, setHistFilterMaterial] = useState(null);
  const [histFilterBomId, setHistFilterBomId] = useState("");
  const [histFilterTags, setHistFilterTags] = useState([]);
  const [histFilteredQuotes, setHistFilteredQuotes] = useState([]);

  const loadHistoryFilters = useCallback(async () => {
    if (historyFiltersLoaded) return;
    setHistorySearching(true);
    try {
      const response = await fetch_backend("bill_materials/history/options/", "GET", token, dispatch);
      if (response && response.status === 200) {
        setHistoryFilterOptions({
          clients: (response.clients || []).map(c => ({ value: c.id, label: c.name })),
          quotes: (response.quotes || []).map(q => ({ value: q.id, label: `Cotización #${q.id}`, client_id: q.client_id })),
          materials: (response.materials || []).map(m => ({ value: m.id, label: m.description })),
          tags: (response.tags || []).map(t => ({ value: t.id, label: t.name })),
        });
        setHistoryFiltersLoaded(true);
      }
    } catch (error) {
      console.error("Failed to fetch history filter options", error);
    } finally {
      setHistorySearching(false);
    }
  }, [historyFiltersLoaded, token, dispatch]);

  const handleHistClientChange = (option) => {
    setHistFilterClient(option);
    setHistFilterQuote(null);
    if (option) {
      setHistFilteredQuotes(historyFilterOptions.quotes.filter(q => q.client_id === option.value));
    } else {
      setHistFilteredQuotes([]);
    }
  };

  const searchHistory = async () => {
    setHistorySearching(true);
    const params = new URLSearchParams();
    if (histFilterClient) params.append("client_id", histFilterClient.value);
    if (histFilterQuote) params.append("quote_id", histFilterQuote.value);
    if (histFilterMaterial) params.append("material_id", histFilterMaterial.value);
    if (histFilterBomId) params.append("bom_id", histFilterBomId);
    if (histFilterTags.length > 0) params.append("tag_ids", histFilterTags.map(t => t.value).join(","));

    try {
      const response = await fetch_backend(`bill_materials/history/search/?${params.toString()}`, "GET", token, dispatch);
      if (response && response.status === 200) {
        setHistoryResults(response.results || []);
      }
    } catch (error) {
      console.error("Failed to search history", error);
    } finally {
      setHistorySearching(false);
    }
  };

  const viewBomReadOnly = (bomId) => {
    const url = `${url_bill_of_materials}/detail?bom_id=${bomId}&mode=view`;
    window.open(url, '_blank');
  };

  // Load filters on first render
  React.useEffect(() => {
    loadHistoryFilters();
  }, [loadHistoryFilters]);

  return (
    <div className="history-container">
      <div className="history-search-panel">
        <h3 className="history-search-title">
          <FontAwesomeIcon icon={faSearch} />
          <span>Buscar Hojas de Costos</span>
        </h3>
        <div className="history-filters">
          <div className="history-filter-row">
            <div className="history-filter-field">
              <label className="history-filter-label">Cliente</label>
              <Select
                placeholder="Todos los clientes"
                options={historyFilterOptions.clients}
                styles={styles_react_select}
                value={histFilterClient}
                onChange={handleHistClientChange}
                isClearable
              />
            </div>
            <div className="history-filter-field">
              <label className="history-filter-label">Cotización</label>
              <Select
                placeholder={histFilterClient ? "Seleccione..." : "Seleccione cliente primero"}
                options={histFilteredQuotes}
                styles={styles_react_select}
                value={histFilterQuote}
                onChange={setHistFilterQuote}
                isClearable
                isDisabled={!histFilterClient}
              />
            </div>
            <div className="history-filter-field">
              <label className="history-filter-label">Material</label>
              <Select
                placeholder="Todos los materiales"
                options={historyFilterOptions.materials}
                styles={styles_react_select}
                value={histFilterMaterial}
                onChange={setHistFilterMaterial}
                isClearable
              />
            </div>
            <div className="history-filter-field">
              <label className="history-filter-label">ID de Lista</label>
              <input
                type="text"
                className="history-filter-input"
                placeholder="Ej: 123"
                value={histFilterBomId}
                onChange={(e) => setHistFilterBomId(e.target.value)}
              />
            </div>
            <div className="history-filter-field">
              <label className="history-filter-label">Tags</label>
              <Select
                placeholder="Seleccione tags..."
                options={historyFilterOptions.tags}
                styles={styles_react_select}
                value={histFilterTags}
                onChange={(options) => setHistFilterTags(options || [])}
                isMulti
                closeMenuOnSelect={false}
              />
            </div>
          </div>
          <div className="history-search-actions">
            <button className="btn-history-search" onClick={searchHistory} disabled={historySearching}>
              <FontAwesomeIcon icon={faSearch} />
              <span>{historySearching ? "Buscando..." : "Buscar"}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="history-results-panel">
        {historySearching ? (
          <div className="history-results-loading">Buscando resultados...</div>
        ) : historyResults.length > 0 ? (
          <>
            <div className="history-results-header">
              <span>{historyResults.length} resultado{historyResults.length !== 1 ? 's' : ''} encontrado{historyResults.length !== 1 ? 's' : ''}</span>
            </div>
            <table className="history-results-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Cotización</th>
                  <th>Tags</th>
                  <th>Descripción</th>
                  <th>Fecha</th>
                  <th>Total</th>
                  <th style={{ textAlign: "center" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {historyResults.map((result) => (
                  <tr key={result.id}>
                    <td><span className="history-bom-id">#{result.id}</span></td>
                    <td>{result.client_name}</td>
                    <td>{result.quote_id ? `#${result.quote_id}` : "-"}</td>
                    <td>
                      <div className="history-tags-cell">
                        {(result.tags || []).map((tag, i) => (
                          <span key={i} className="history-tag-badge">{tag}</span>
                        ))}
                      </div>
                    </td>
                    <td className="history-desc-cell">{result.description || "-"}</td>
                    <td>{result.date}</td>
                    <td className="history-total-cell">{formatCurrency(result.total || 0)}</td>
                    <td style={{ textAlign: "center" }}>
                      <div className="history-actions-cell">
                        <button className="btn-history-load" onClick={() => duplicateFromHistory(result.id)}>
                          <FontAwesomeIcon icon={faCopy} />
                          <span>Duplicar</span>
                        </button>
                        <button className="btn-history-view" onClick={() => viewBomReadOnly(result.id)}>
                          <FontAwesomeIcon icon={faEye} />
                          <span>Ver detalle</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="history-empty-results">
            <FontAwesomeIcon icon={faSearch} size="2x" color="#ccc" />
            <p>Usa los filtros para buscar listas de materiales existentes.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default HistoryTab;
